import { inject, Injectable, Type } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateChild,
  CanActivateChildFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CommonRoute } from '../enums/common-route.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { AuthorizationService } from '../services/authorization/authorization.service';
import { LogFactory } from '../services/logger/log-factory';
import { PlatformService } from '../services/platform/platform.service';
import { TokenStorageService } from '../services/token-storage/token-storage.service';

@Injectable({
  providedIn: 'root',
})
class AccessControlGuardService implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly platformService = inject(PlatformService);

  private readonly logger = inject(LogFactory).createLogger(
    AccessControlGuardService,
  );

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    // A browser hard reload starts Angular routing while persisted tokens are
    // still being read from web secure storage. Wait for that hydration before
    // deciding the caller is a guest; otherwise a valid returning session is
    // redirected to login simply because the in-memory subjects are still null.
    return this.authenticationService.whenInitialized().pipe(
      switchMap(() => this.authenticationService.hasValidSession()),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          this.logger.debug(
            'User is not authenticated after check; redirecting to login.',
          );
          return of(this.createRedirectTree(CommonRoute.LOGIN, state.url));
        }

        // If authenticated, proceed to authorization checks.
        return this.checkUrlAccess(state.url).pipe(
          switchMap((urlAccessResult) => {
            if (urlAccessResult !== true) {
              return of(urlAccessResult); // No URL access, return UrlTree
            }
            return this.checkComponentAndPageAccess(route, state.url).pipe(
              switchMap((accessResult) => {
                if (accessResult !== true) {
                  return of(accessResult);
                }
                return this.mfaCheck(state);
              }),
            );
          }),
        );
      }),
    );
  }

  private mfaCheck(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const accessToken = this.authenticationService.getAccessToken() || '';
    const decodedToken = this.tokenStorage.decodeToken(accessToken);

    if (decodedToken?.mfaSetupRequired && this.platformService.isAdminUi()) {
      this.logger.debug(
        'User has MFA setup required; redirecting to MFA setup.',
      );
      if (state.url.includes(CommonRoute.ADMIN_MFA_SETUP)) {
        return of(true);
      }
      return of(
        this.createRedirectTree(CommonRoute.ADMIN_MFA_SETUP, state.url),
      );
    }

    if (decodedToken?.mfaRequired) {
      if (state.url.includes(CommonRoute.REQUEST_MFA_RESET)) {
        this.logger.debug(
          'User is requesting MFA reset; allowing access.',
        );
        return of(true);
      }
      this.logger.debug(
        'User has MFA required; redirecting to login for MFA verification.',
      );
      return of(this.createRedirectTree(CommonRoute.LOGIN, state.url));
    }

    return of(true);
  }

  private createRedirectTree(route: CommonRoute, returnUrl: string): UrlTree {
    return this.router.createUrlTree([route], {
      queryParams: { returnUrl },
    });
  }

  private normalizeUrlForAccess(url: string): string[] {
    // FRONTEND_URL grants are path-only; a query string or fragment (e.g.
    // /account/profile#body-details for anchor scrolling) is never part of an
    // RBAC grant, so strip both before matching or the check false-negatives.
    const path = url.split('#')[0].split('?')[0];

    // Generate variants by normalizing dynamic segments (ids, uuids)
    const variants = new Set<string>([path]);

    // Replace numeric ids with :id
    variants.add(path.replace(/\/(\d+)(?=\/|$)/g, '/:id'));

    // Replace uuid-like ids with :id
    const uuidRegex =
      /\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})(?=\/|$)/g;
    variants.add(path.replace(uuidRegex, '/:id'));

    // Collapse multiple slashes
    return Array.from(variants).map((u) =>
      u.replace(/\/+/, '/').replace(/\/+/, '/').replace(/\/+/, '/'),
    );
  }

  private checkUrlAccess(url: string): Observable<boolean | UrlTree> {
    const variants = this.normalizeUrlForAccess(url);
    return from(
      Promise.all(
        variants.map((u) => this.authorizationService.hasAccessToUrl(u)),
      ),
    ).pipe(
      map((results) => {
        const hasAnyAccess = results.some((r) => !!r);
        if (!hasAnyAccess) {
          this.logger.warn('Access denied to URL', { url });
          return this.createRedirectTree(CommonRoute.ACCESS_DENIED, url);
        }
        return true;
      }),
    );
  }

  private checkComponentAndPageAccess(
    route: ActivatedRouteSnapshot,
    returnUrl: string,
  ): Observable<boolean | UrlTree> {
    return this.resolveComponent(route).pipe(
      switchMap(async (resolvedComponent) => {
        if (!resolvedComponent) {
          // Layout routes (children-only, no component of their own) are a
          // legitimate Angular pattern — there's nothing to gate.
          return true;
        }

        const componentName = (resolvedComponent as any).name || '(unnamed)';

        // Check for component-specific access
        const componentAccessKey = (resolvedComponent as any)
          .componentAccessKey;
        if (componentAccessKey) {
          const hasAccess =
            await this.authorizationService.hasAccessToComponent(
              componentAccessKey,
            );
          if (!hasAccess) {
            this.logger.warn('Access denied to component', {
              componentAccessKey,
            });
            return this.createRedirectTree(
              CommonRoute.ACCESS_DENIED,
              returnUrl,
            );
          }
        }

        // Check for page-specific access
        const pageAccessKey = (resolvedComponent as any).pageAccessKey;
        if (pageAccessKey) {
          const hasAccess = await this.authorizationService.hasAccessToPage(
            pageAccessKey,
          );
          if (!hasAccess) {
            this.logger.warn('Access denied to page', { pageAccessKey });
            return this.createRedirectTree(
              CommonRoute.ACCESS_DENIED,
              returnUrl,
            );
          }
        }

        if (!componentAccessKey && !pageAccessKey) {
          this.logger.warn(
            'Component/page is reachable via router but has no @ComponentAccess or @PageAccess decorator.',
            { componentName },
          );
        }

        return true; // All checks passed
      }),
    );
  }

  private resolveComponent(
    route: ActivatedRouteSnapshot,
  ): Observable<Type<any> | null> {
    if (route.component) {
      return of(route.component);
    }
    if (route.routeConfig?.loadComponent) {
      const loaded = route.routeConfig.loadComponent();
      // Check if it's a promise-like or an observable
      if (typeof (loaded as any).then === 'function') {
        return from(loaded as Promise<Type<any>>);
      }
      return from(loaded as Observable<Type<any>>);
    }
    return of(null);
  }
}

export const AccessControlGuard: CanActivateChildFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  return inject(AccessControlGuardService).canActivateChild(route, state);
};
